/**
 * React Query hook for fetching trips within a 7-day window
 */

import { useQuery } from '@tanstack/react-query';
import { getTripsForWeek } from '@/services/tripService';
import type { Trip } from '@/services/tripService';

/**
 * Hook to get trips for a 7-day window starting from a given date
 * @param startDate - Start date of the week (defaults to today)
 */
export function useWeekTrips(startDate?: Date) {
  const queryDate = startDate || new Date();
  // Normalize to start of day
  queryDate.setHours(0, 0, 0, 0);

  return useQuery<Trip[], Error>({
    queryKey: ['trips', 'week', queryDate.toISOString().split('T')[0]],
    queryFn: () => getTripsForWeek(queryDate),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true, // Refresh when user returns to tab
  });
}

/**
 * Hook to get trips for this week (today + 6 days)
 */
export function useThisWeekTrips() {
  return useWeekTrips(new Date());
}

export default useWeekTrips;
